/**
 * Motor de cálculo de comissão e bônus.
 *
 * Todo o cálculo é feito em CENTAVOS INTEIROS: inteiros são exatos em
 * JavaScript até 9 quadrilhões, muito acima de qualquer valor da agência.
 * Assim não há erro de ponto flutuante (0,1 + 0,2 ≠ 0,3).
 * Entradas e saídas são strings decimais ("1234.56"), como na API.
 *
 * Arredondamento: meio centavo para cima (padrão comercial).
 */

export type FaixaBonus = 'ABAIXO_DO_PISO' | 'PROPORCIONAL' | 'ACELERADOR';

export interface PoliticaBonus {
  /** Fração: "0.7000" = 70% da meta. */
  pisoPct: string;
  /** "800.00" */
  valorBase: string;
  /** Fração: "0.0150" = 1,5% sobre o excedente. */
  aceleradorPct: string;
  /** "200.00" */
  valorColetivo: string;
}

/** Política padrão (modelo de bônus da Fly Now). */
export const POLITICA_BONUS_PADRAO: PoliticaBonus = {
  pisoPct: '0.7000',
  valorBase: '800.00',
  aceleradorPct: '0.0150',
  valorColetivo: '200.00',
};
export const META_INDIVIDUAL_PADRAO = '100000.00';
export const META_GLOBAL_PADRAO = '700000.00';
export const COMISSAO_PCT_PADRAO = '0.1000';
/** Lucro acima desta fração do valor da venda gera alerta de "margem alta". */
export const MARGEM_ALTA_PCT = 0.4;

const MONEY_RE = /^-?\d+(\.\d{1,2})?$/;
const FRACTION_RE = /^\d+(\.\d{1,6})?$/;

/** "1234.5" -> 123450 (centavos). Lança erro em formato inválido. */
export function toCents(value: string): number {
  if (!MONEY_RE.test(value)) throw new Error(`Valor monetário inválido: "${value}"`);
  const negative = value.startsWith('-');
  const [inteiro = '0', dec = ''] = value.replace('-', '').split('.');
  const cents = Number(inteiro) * 100 + Number(dec.padEnd(2, '0'));
  return negative ? -cents : cents;
}

/** 123450 -> "1234.50" */
export function fromCents(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const s = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return negative ? `-${s}` : s;
}

/** "0.0150" -> 150 partes por 10.000 (até 6 casas: "0.876543" -> 8765.43 vira escala 1e6). */
function fractionToMicros(value: string): number {
  if (!FRACTION_RE.test(value)) throw new Error(`Percentual inválido: "${value}"`);
  const [inteiro = '0', dec = ''] = value.split('.');
  return Number(inteiro) * 1_000_000 + Number(dec.padEnd(6, '0'));
}

/** Arredonda a/b (inteiros, b > 0) para o inteiro mais próximo, meio para cima (também em negativos, simétrico). */
function divRound(a: number, b: number): number {
  const q = Math.abs(a) / b;
  const r = Math.floor(q + 0.5);
  return a < 0 ? -r : r;
}

export interface ResultadoComissao {
  lucro: string;
  comissao: string;
  /** Lucro negativo: a comissão é zero e a venda gera alerta. */
  prejuizo: boolean;
}

/** Comissão = percentual × lucro, onde lucro = valor da venda − custo. Nunca negativa. */
export function calcularComissao(valorBruto: string, custoTotal: string, percentual: string): ResultadoComissao {
  const lucroCents = toCents(valorBruto) - toCents(custoTotal);
  const comissaoCents = lucroCents > 0 ? divRound(lucroCents * fractionToMicros(percentual), 1_000_000) : 0;
  return { lucro: fromCents(lucroCents), comissao: fromCents(comissaoCents), prejuizo: lucroCents < 0 };
}

export interface ResultadoBonus {
  /** Fração exata com 6 casas: "0.876500" = 87,65%. */
  atingimento: string;
  faixa: FaixaBonus;
  /** Parcela da meta individual (proporcional entre o piso e 100%; valor base a partir de 100%). */
  bonusIndividual: string;
  /** 1,5% sobre o que passou da meta. */
  bonusAcelerador: string;
  /** Quanto falta para 100% da meta ("0.00" se já bateu). */
  faltaParaMeta: string;
  /** Quanto falta para o piso (70%) ("0.00" se já passou). */
  faltaParaPiso: string;
}

/**
 * Bônus individual do vendedor no mês.
 *  - abaixo de 70% da meta (piso): nada;
 *  - de 70% a menos de 100%: R$ 800 × atingimento exato (87,65% -> R$ 701,20);
 *  - 100% ou mais: R$ 800 + 1,5% do valor que passou da meta.
 */
export function calcularBonusIndividual(vendido: string, meta: string, politica: PoliticaBonus = POLITICA_BONUS_PADRAO): ResultadoBonus {
  const vendidoCents = Math.max(0, toCents(vendido));
  const metaCents = toCents(meta);
  if (metaCents <= 0) throw new Error('A meta individual precisa ser maior que zero.');

  const pisoMicros = fractionToMicros(politica.pisoPct);
  const baseCents = toCents(politica.valorBase);
  const aceleradorMicros = fractionToMicros(politica.aceleradorPct);

  const atingimentoMicros = divRound(vendidoCents * 1_000_000, metaCents);
  const atingimento = `${Math.floor(atingimentoMicros / 1_000_000)}.${String(atingimentoMicros % 1_000_000).padStart(6, '0')}`;
  // Comparações feitas com inteiros exatos (vendido × 1e6 vs meta × piso), sem arredondar o atingimento.
  const abaixoDoPiso = vendidoCents * 1_000_000 < metaCents * pisoMicros;
  const pisoCents = Math.ceil((metaCents * pisoMicros) / 1_000_000);

  let faixa: FaixaBonus;
  let individual = 0;
  let acelerador = 0;
  if (abaixoDoPiso) {
    faixa = 'ABAIXO_DO_PISO';
  } else if (vendidoCents < metaCents) {
    faixa = 'PROPORCIONAL';
    individual = divRound(baseCents * vendidoCents, metaCents);
  } else {
    faixa = 'ACELERADOR';
    individual = baseCents;
    acelerador = divRound((vendidoCents - metaCents) * aceleradorMicros, 1_000_000);
  }

  return {
    atingimento,
    faixa,
    bonusIndividual: fromCents(individual),
    bonusAcelerador: fromCents(acelerador),
    faltaParaMeta: fromCents(Math.max(0, metaCents - vendidoCents)),
    faltaParaPiso: fromCents(Math.max(0, pisoCents - vendidoCents)),
  };
}

/** A equipe bateu a meta global? (soma das vendas atribuídas a vendedores) */
export function metaGlobalAtingida(vendidoEquipe: string, metaGlobal: string): boolean {
  return toCents(vendidoEquipe) >= toCents(metaGlobal);
}

/** Soma de valores monetários sem erro de arredondamento. */
export function somar(valores: string[]): string {
  return fromCents(valores.reduce((acc, v) => acc + toCents(v), 0));
}
